// Представление подписки для витрин (/balance, пейвол, модалка пополнения,
// лендинги) и блока «Моя подписка». Чистые функции без React/сети — покрыты
// subscriptionView.test.ts. Один источник правды про период, состав и тексты,
// чтобы четыре витрины не склоняли «месяц» каждая по-своему.
import { SONGS_GRANT_KEYS, getPackagePriceRub, } from "@/shared/api/packages";
import { formatRub, parseRubAmount } from "./formatRub";
import { parseServerDate } from "./serverDate";
import { pluralRu } from "./utils";
/**
 * Пакет — подписка (оплата с автопродлением)? Новый контракт — `kind`, fallback
 * на legacy `is_recurring`.
 */
export function isSubscriptionPackage(pkg) {
    return pkg.kind === "subscription" || Boolean(pkg.is_recurring);
}
const PERIOD_UNITS = [
    "day",
    "week",
    "month",
    "year",
];
/**
 * Собрать период из пары «единица + количество». `null`, если единица
 * неизвестна: тогда витрина показывает цену без периода, а не выдумывает его.
 */
export function toSubscriptionPeriod(unit, value) {
    const normalized = (unit ?? "").toLowerCase();
    if (!PERIOD_UNITS.includes(normalized))
        return null;
    const count = Math.floor(Number(value ?? 1));
    return { unit: normalized, value: Number.isFinite(count) && count >= 1 ? count : 1 };
}
/** Период списания подписочного пакета (`duration_type` × `duration_value`). */
export function getPackagePeriod(pkg) {
    return toSubscriptionPeriod(pkg.duration_type, pkg.duration_value);
}
// Русские формы единицы: [винительный ед. ч., «2–4», «5+»]. Винительный — потому
// что все наши конструкции его требуют: «в неделю», «за неделю», «каждую неделю».
const RU_UNIT = {
    day: ["день", "дня", "дней"],
    week: ["неделю", "недели", "недель"],
    month: ["месяц", "месяца", "месяцев"],
    year: ["год", "года", "лет"],
};
// «каждый месяц», но «каждую неделю».
const RU_EVERY_ONE = {
    day: "каждый",
    week: "каждую",
    month: "каждый",
    year: "каждый",
};
const ruUnit = ({ unit, value }) => pluralRu(value, ...RU_UNIT[unit]);
const enUnit = ({ unit, value }) => value === 1 ? unit : `${unit}s`;
/** «месяц» / «3 месяца» — период для записи через дробь: «990 ₽ / месяц». */
export function formatPeriodShort(period, language) {
    const unit = language === "ru" ? ruUnit(period) : enUnit(period);
    return period.value === 1 ? unit : `${period.value} ${unit}`;
}
/**
 * «на месяц» / «на 3 месяца» — на какой срок выдаётся начисление. Именно «на»,
 * а не «в месяц»: песни выдаются один раз на весь оплаченный период, остаток
 * на следующий не переносится.
 */
export function formatPeriodFor(period, language) {
    if (language !== "ru") {
        return period.value === 1
            ? `for a ${enUnit(period)}`
            : `for ${period.value} ${enUnit(period)}`;
    }
    return `на ${formatPeriodShort(period, language)}`;
}
/** «каждый месяц» / «каждые 3 месяца» — как часто списание или начисление. */
export function formatPeriodEvery(period, language) {
    if (language !== "ru")
        return `every ${formatPeriodShort(period, language)}`;
    return period.value === 1
        ? `${RU_EVERY_ONE[period.unit]} ${ruUnit(period)}`
        : `каждые ${period.value} ${ruUnit(period)}`;
}
/** «990 ₽» — сумма с разрядами и знаком рубля через неразрывный пробел. */
export function formatRubPrice(value) {
    return `${formatRub(value)}\u00a0₽`;
}
/** «990 ₽ / месяц»; без периода — просто «990 ₽». */
export function formatPriceWithPeriod(priceRub, period, language) {
    const price = formatRubPrice(priceRub);
    return period ? `${price} / ${formatPeriodShort(period, language)}` : price;
}
/** «песня / песни / песен» по числу. */
export function songsWord(n, language = "ru") {
    if (language !== "ru")
        return n === 1 ? "song" : "songs";
    return pluralRu(n, "песня", "песни", "песен");
}
const sumSongs = (grants) => grants
    ? SONGS_GRANT_KEYS.reduce((sum, key) => sum + (Number(grants[key]) || 0), 0)
    : 0;
/**
 * Что входит в подписку — ровно так, как начисляет бэк:
 *
 *  • Есть ЕЖЕДНЕВНЫЕ начисления песен (`recurring_period_type = daily`): суточная
 *    задача каждый день выставляет подписочный баланс равным `recurring_grants`
 *    (не прибавляет — неиспользованное сгорает), а `initial_grants` живут только
 *    до первого такого начисления. Поэтому пишем «N песен каждый день», а
 *    стартовые как «на месяц» НЕ показываем.
 *  • Ежедневных нет: песни выдаются раз на период из `initial_grants`, при
 *    продлении баланс сбрасывается и начисляется заново — «N песен на месяц».
 *    Недельные/месячные recurring бэк не начисляет вовсе (как и
 *    `recurring_period_value > 1`) — не обещаем их.
 *
 * `null` — песен пакет не даёт, строку состава не рисуем.
 */
export function getSubscriptionIncludes(pkg, language) {
    const ru = language === "ru";
    const daily = (pkg.recurring_period_type ?? "").toLowerCase() === "daily"
        ? sumSongs(pkg.recurring_grants)
        : 0;
    if (daily > 0) {
        return {
            main: `${daily} ${songsWord(daily, language)} ${formatPeriodEvery({ unit: "day", value: 1 }, language)}`,
            note: ru
                ? "Неиспользованные песни не переносятся на следующий день"
                : "Unused songs don't carry over to the next day",
        };
    }
    const initial = sumSongs(pkg.initial_grants);
    if (initial <= 0)
        return null;
    const period = getPackagePeriod(pkg);
    const base = `${initial} ${songsWord(initial, language)}`;
    return {
        main: period ? `${base} ${formatPeriodFor(period, language)}` : base,
        note: ru
            ? "При продлении остаток не переносится"
            : "Unused songs don't carry over on renewal",
    };
}
/** «990 ₽ каждый месяц»; без периода — «990 ₽ за каждый период». */
export function formatChargeEvery(priceRub, period, language) {
    const price = formatRubPrice(priceRub);
    if (period)
        return `${price} ${formatPeriodEvery(period, language)}`;
    return language === "ru" ? `${price} за каждый период` : `${price} each period`;
}
export function toSubscriptionPackageView(pkg, language) {
    const priceRub = getPackagePriceRub(pkg);
    const period = getPackagePeriod(pkg);
    const charge = formatChargeEvery(priceRub, period, language);
    const includes = getSubscriptionIncludes(pkg, language);
    return {
        priceRub,
        price: formatRubPrice(priceRub),
        periodShort: period ? formatPeriodShort(period, language) : "",
        priceWithPeriod: formatPriceWithPeriod(priceRub, period, language),
        renewalRule: language === "ru"
            ? `Далее ${charge} до отмены`
            : `Then ${charge} until cancelled`,
        includes: includes ? [includes.main, includes.note] : [],
    };
}
/**
 * Что показать в витрине. Разовые пакеты — всегда и сразу. Подписочные:
 *  • без ЮKassa — не показываем вовсе: автосписания умеет только она, кнопка
 *    была бы мёртвой;
 *  • пока не знаем, есть ли подписка, — заглушка (`pending`);
 *  • подписка уже есть — убираем: вторую оформить нельзя (бэк ответит
 *    `subscription_already_active`).
 * Порядок пакетов не меняется.
 */
export function getStorefrontItems(packages, { hasSubscription, isResolved }) {
    const items = [];
    for (const pkg of packages) {
        if (!isSubscriptionPackage(pkg)) {
            items.push({ pkg, state: "offer" });
            continue;
        }
        if (!pkg.is_yookassa_available)
            continue;
        if (!isResolved) {
            items.push({ pkg, state: "pending" });
            continue;
        }
        if (!hasSubscription)
            items.push({ pkg, state: "offer" });
    }
    return items;
}
/**
 * Показывать «Пакеты не найдены»? Только когда витрина действительно пуста по
 * незнакомой причине. Если пусто потому, что единственный пакет — подписка, а
 * она у пользователя уже есть, рядом стоит блок «Моя подписка», и «не найдены»
 * выглядело бы как поломка.
 */
export function shouldShowEmptyStorefront(items, { hasSubscription }) {
    return items.length === 0 && !hasSubscription;
}
export function getMySubscriptionState(subscription) {
    if (subscription.status === "cancelled")
        return "cancelled";
    return subscription.will_renew ? "renewing" : "active";
}
/**
 * Показывать кнопку «Отменить подписку»? У отменённой — никогда. У той, что
 * продлится, — всегда: раз списание будет, у человека должен быть способ его
 * остановить. У активной без продления — по флагу бэка `can_cancel`.
 */
export function canCancelSubscription(subscription) {
    const state = getMySubscriptionState(subscription);
    if (state === "cancelled")
        return false;
    if (state === "renewing")
        return true;
    return Boolean(subscription.can_cancel);
}
/**
 * Как назвать подписку в тексте: «Подписка «Музыкант»». Если пакет уже назван
 * «Подписка на месяц» — оставляем как есть, иначе вышло бы «Подписка «Подписка
 * на месяц»». Пустое название → просто «Подписка».
 */
export function formatSubscriptionName(packageTitle, language) {
    const title = (packageTitle ?? "").trim();
    const word = language === "ru" ? "Подписка" : "Subscription";
    if (!title)
        return word;
    if (/подписк|subscription/i.test(title))
        return title;
    return language === "ru" ? `${word} «${title}»` : `“${title}” subscription`;
}
/**
 * Дата подписки в локали пользователя: «15 октября 2026» / «October 15,
 * 2026». Бэк отдаёт наивный UTC — разбираем общим `parseServerDate`, иначе
 * дата съедет на часовой пояс. Нечитаемая дата → пустая строка.
 *
 * Русское «г.» в конце срезаем: дата стоит и в конце предложения («доступ
 * сохранится до 15 октября 2026 г..» — с двумя точками), и перед тире.
 */
export function formatSubscriptionDate(iso, language) {
    if (!iso)
        return "";
    const date = parseServerDate(iso);
    if (Number.isNaN(date.getTime()))
        return "";
    return date
        .toLocaleDateString(language === "ru" ? "ru-RU" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
    })
        .replace(/\s*г\.$/, "");
}
/**
 * Тексты блока «Моя подписка» по состоянию подписки.
 *
 * Дату называем, только если она читается и ещё не прошла. Подписка «активна»,
 * а `expires_at` в прошлом (продление ещё обрабатывается) или битый — тогда
 * «Следующее списание: <вчера>» было бы неправдой про деньги, и мы показываем
 * нейтральное «активна» без даты и суммы.
 */
export function describeMySubscription(subscription, language, now = new Date()) {
    const ru = language === "ru";
    const state = getMySubscriptionState(subscription);
    const name = formatSubscriptionName(subscription.package_title, language);
    const price = formatRubPrice(parseRubAmount(subscription.price_rub));
    const expires = subscription.expires_at
        ? parseServerDate(subscription.expires_at)
        : null;
    // Нечитаемая дата даёт NaN, а сравнение с NaN всегда ложно — «не будущее».
    const isFuture = expires !== null && expires.getTime() > now.getTime();
    const until = isFuture
        ? formatSubscriptionDate(subscription.expires_at, language)
        : "";
    const headline = state === "cancelled"
        ? ru
            ? "Автопродление отключено"
            : "Auto-renewal is off"
        : ru
            ? `${name} активна`
            : `${name} is active`;
    let details;
    if (state === "cancelled") {
        details = until
            ? ru
                ? `${name} действует до ${until}. Списаний больше не будет.`
                : `${name} stays active until ${until}. No further charges.`
            : ru
                ? "Списаний больше не будет."
                : "No further charges.";
    }
    else if (!until) {
        details = null;
    }
    else if (state === "renewing") {
        // Неразрывный пробел перед тире: при переносе тире не остаётся в конце строки.
        details = ru
            ? `Следующее списание: ${until}\u00a0— ${price}`
            : `Next charge: ${until}\u00a0— ${price}`;
    }
    else {
        details = ru ? `Действует до ${until}` : `Active until ${until}`;
    }
    const cancelConfirm = until
        ? ru
            ? `Автопродление будет отключено, доступ сохранится до ${until}.`
            : `Auto-renewal will be turned off. You keep access until ${until}.`
        : ru
            ? "Автопродление будет отключено, доступ сохранится до конца оплаченного периода."
            : "Auto-renewal will be turned off. You keep access until the end of the paid period.";
    return {
        state,
        headline,
        details,
        cancelConfirm,
        canCancel: canCancelSubscription(subscription),
    };
}
// ── Ошибки оформления подписки ────────────────────────────────
/**
 * Коды подписочных ошибок (`POST /payments/`, `POST /subscriptions/cancel`).
 * Бэк отдаёт их В ВЕРХНЕМ регистре в `detail.code`; `getApiErrorCode` приводит
 * код к нижнему, поэтому здесь значения в нижнем и сравнение от регистра не
 * зависит. HTTP-статусы: auth_required — 403, suspended / already_active — 409,
 * platform_mismatch / provider_unsupported / consent_required — 400,
 * not_found — 404.
 */
export const SUBSCRIPTION_ERROR = {
    authRequired: "subscription_auth_required",
    suspended: "subscriptions_suspended",
    alreadyActive: "subscription_already_active",
    platformMismatch: "package_platform_mismatch",
    providerUnsupported: "subscription_provider_unsupported",
    consentRequired: "autorenewal_consent_required",
    notFound: "subscription_not_found",
    /** 502 на отмене: провайдер не подтвердил, подписка НЕ отменена. */
    cancelProviderFailed: "subscription_cancel_provider_failed",
};
const SUBSCRIPTION_ERROR_TEXT = {
    [SUBSCRIPTION_ERROR.authRequired]: {
        ru: "Подписка доступна только после входа в аккаунт",
        en: "Sign in to subscribe",
    },
    [SUBSCRIPTION_ERROR.suspended]: {
        ru: "Оформление подписок временно недоступно",
        en: "Subscriptions are temporarily unavailable",
    },
    [SUBSCRIPTION_ERROR.alreadyActive]: {
        ru: "У вас уже есть подписка",
        en: "You already have a subscription",
    },
    [SUBSCRIPTION_ERROR.platformMismatch]: {
        ru: "Этот тариф недоступен на этой платформе",
        en: "This plan isn't available on this platform",
    },
    [SUBSCRIPTION_ERROR.providerUnsupported]: {
        ru: "Подписку нельзя оплатить этим способом",
        en: "This payment method doesn't support subscriptions",
    },
    [SUBSCRIPTION_ERROR.consentRequired]: {
        ru: "Подтвердите согласие на автоматические списания",
        en: "Please confirm your consent to automatic charges",
    },
    [SUBSCRIPTION_ERROR.notFound]: {
        ru: "Подписка не найдена",
        en: "Subscription not found",
    },
    [SUBSCRIPTION_ERROR.cancelProviderFailed]: {
        ru: "Не удалось отменить подписку. Попробуйте ещё раз через пару минут или напишите в поддержку.",
        en: "Couldn't cancel the subscription. Please try again in a couple of minutes or contact support.",
    },
};
/**
 * Понятный текст по коду ошибки подписки. `null` — код не про подписку (или
 * его нет): вызывающий показывает своё общее сообщение либо молчит.
 */
export function getSubscriptionErrorMessage(code, language) {
    if (!code)
        return null;
    const text = SUBSCRIPTION_ERROR_TEXT[code.toLowerCase()];
    return text ? text[language] : null;
}
